/**
 * AI model (LLM) configuration managed in-app by Project Admins (AI models page), for the whole
 * organization or for one project. Each scope has at most one default; a run uses its project's
 * enabled default, else the organization's (agent.act / agent.assert steps).
 * Secret fields are write-only: stored encrypted, never returned by the API.
 */
import { z } from 'zod';

export const LLM_PROVIDERS = ['OPENAI', 'ANTHROPIC', 'BEDROCK', 'LITELLM', 'GEMINI'] as const;
export type LlmProvider = (typeof LLM_PROVIDERS)[number];

export const LLM_SECRET_FIELDS = ['apiKey', 'accessKeyId', 'secretAccessKey', 'sessionToken'] as const;
export type LlmSecretField = (typeof LLM_SECRET_FIELDS)[number];
export type LlmSecrets = Partial<Record<LlmSecretField, string>>;

export interface LlmFieldSpec {
  key: 'model' | 'baseUrl' | 'region' | LlmSecretField;
  label: string;
  required: boolean;
  secret: boolean;
  placeholder?: string;
  help?: string;
}

export interface LlmProviderSpec {
  provider: LlmProvider;
  label: string;
  description: string;
  defaultModel?: string;
  fields: LlmFieldSpec[];
}

const model = (placeholder: string, help?: string): LlmFieldSpec => ({
  key: 'model', label: 'Model', required: true, secret: false, placeholder, help,
});

/** Field definitions per provider - drives both the Admin form and API validation. */
export const LLM_PROVIDER_SPECS: Record<LlmProvider, LlmProviderSpec> = {
  OPENAI: {
    provider: 'OPENAI',
    label: 'OpenAI',
    description: 'OpenAI API (platform.openai.com). Needs an API key, not a ChatGPT subscription.',
    defaultModel: 'gpt-5.5',
    fields: [
      model('gpt-5.5'),
      { key: 'apiKey', label: 'API key', required: true, secret: true, placeholder: 'sk-...' },
      { key: 'baseUrl', label: 'Base URL (optional)', required: false, secret: false, placeholder: 'https://api.openai.com/v1', help: 'Only for Azure OpenAI or a proxy.' },
    ],
  },
  ANTHROPIC: {
    provider: 'ANTHROPIC',
    label: 'Anthropic',
    description: 'Claude models via the Anthropic API (console.anthropic.com).',
    defaultModel: 'claude-sonnet-5-5',
    fields: [
      model('claude-sonnet-5-5'),
      { key: 'apiKey', label: 'API key', required: true, secret: true, placeholder: 'sk-ant-...' },
    ],
  },
  BEDROCK: {
    provider: 'BEDROCK',
    label: 'AWS Bedrock',
    description: 'Models hosted in your AWS account. The IAM user needs bedrock:InvokeModel and model access enabled.',
    fields: [
      model('us.anthropic.claude-sonnet-5-5-v1:0', 'Bedrock model ID or inference-profile ID (e.g. us.anthropic...).'),
      { key: 'region', label: 'AWS region', required: true, secret: false, placeholder: 'us-east-1' },
      { key: 'accessKeyId', label: 'Access key ID', required: true, secret: true, placeholder: 'AKIA...' },
      { key: 'secretAccessKey', label: 'Secret access key', required: true, secret: true },
      { key: 'sessionToken', label: 'Session token (optional)', required: false, secret: true, help: 'Only for temporary credentials.' },
    ],
  },
  LITELLM: {
    provider: 'LITELLM',
    label: 'LiteLLM',
    description: 'A LiteLLM proxy (or any OpenAI-compatible server, e.g. Ollama, vLLM).',
    fields: [
      model('gpt-4o', 'The model name as configured in your LiteLLM proxy.'),
      { key: 'baseUrl', label: 'Proxy URL', required: true, secret: false, placeholder: 'http://litellm:4000/v1' },
      { key: 'apiKey', label: 'API key (optional)', required: false, secret: true, placeholder: 'sk-...' },
    ],
  },
  GEMINI: {
    provider: 'GEMINI',
    label: 'Google Gemini',
    description: 'Gemini models via Google AI Studio (aistudio.google.com).',
    fields: [
      model('gemini-3-pro', 'Gemini model ID.'),
      { key: 'apiKey', label: 'API key', required: true, secret: true, placeholder: 'AIza...' },
    ],
  },
};

const secretValue = z.string().trim().min(1).max(4096);

/** Create: all required fields present. */
export const llmConfigCreateSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    provider: z.enum(LLM_PROVIDERS),
    model: z.string().trim().min(1).max(200),
    baseUrl: z.string().trim().url().max(500).nullish(),
    region: z.string().trim().regex(/^[a-z]{2}(-[a-z]+)+-\d$/, 'e.g. us-east-1').nullish(),
    secrets: z.object({
      apiKey: secretValue.optional(),
      accessKeyId: secretValue.optional(),
      secretAccessKey: secretValue.optional(),
      sessionToken: secretValue.optional(),
    }).strict().default({}),
    enabled: z.boolean().default(true),
    isDefault: z.boolean().default(false),
    /** Set to connect the model for one project instead of the whole organization. */
    projectId: z.string().min(1).nullish(),
  })
  .strict();
export type LlmConfigCreate = z.infer<typeof llmConfigCreateSchema>;

/** Update: secrets omitted = keep, value = replace, null = clear. */
export const llmConfigUpdateSchema = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    model: z.string().trim().min(1).max(200).optional(),
    baseUrl: z.string().trim().url().max(500).nullish(),
    region: z.string().trim().regex(/^[a-z]{2}(-[a-z]+)+-\d$/, 'e.g. us-east-1').nullish(),
    secrets: z.object({
      apiKey: secretValue.nullish(),
      accessKeyId: secretValue.nullish(),
      secretAccessKey: secretValue.nullish(),
      sessionToken: secretValue.nullish(),
    }).strict().optional(),
    enabled: z.boolean().optional(),
  })
  .strict();
export type LlmConfigUpdate = z.infer<typeof llmConfigUpdateSchema>;

/** Returns the labels of required fields that are missing for a (merged) configuration. */
export function missingLlmFields(
  provider: LlmProvider,
  cfg: { model?: string | null; baseUrl?: string | null; region?: string | null },
  secretsSet: Partial<Record<LlmSecretField, boolean>>,
): string[] {
  return LLM_PROVIDER_SPECS[provider].fields
    .filter((f) => f.required)
    .filter((f) => (f.secret ? !secretsSet[f.key as LlmSecretField] : !cfg[f.key as 'model' | 'baseUrl' | 'region']))
    .map((f) => f.label);
}

/** API response shape (no secret values). */
export interface LlmConfigView {
  id: string;
  name: string;
  provider: LlmProvider;
  model: string;
  baseUrl: string | null;
  region: string | null;
  secretsSet: Partial<Record<LlmSecretField, boolean>>;
  /** null = organization-wide. */
  projectId: string | null;
  enabled: boolean;
  isDefault: boolean;
  lastTestAt: string | null;
  lastTestOk: boolean | null;
  lastTestMessage: string | null;
  updatedAt: string;
}

/** GET /projects/:projectId/llm-status: the model a run of this project would use. */
export interface LlmStatus {
  configured: boolean;
  name?: string;
  provider?: LlmProvider;
  model?: string;
  /** Where the model comes from: the project's own default or the organization's. */
  scope?: 'PROJECT' | 'ORG';
}
